import { Button, Dropdown, Menu, message, Popover, Skeleton, Table, Tag } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CustomButton from "../common/CustomButton";
import { fromUtc } from "../helpers/dayjsSetup";
import PurchaseOrderService from "../services/PurchaseOrder/PurchaseOrderService";
import FilterDropdown from "./FilterDropDown";

const PurchaseOrderTableComponent = ({ status }) => {
  const [dataSource, setDataSource] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { supplierID } = location.state || {};
  const [list, setList] = useState({
    request: {
      priorityID: null,
      purchaseOrderNum: null,
      supplierID: supplierID ?? null,
      pageFilter: { all: false, size: 10, num: 0 },
      sortFilter: { field: "", order: "" },
    },
    response: { total: 0, data: [] },
  });

  useEffect(() => {
    fetchPurchaseOrderList();
  }, [status]);

  const fetchPurchaseOrderList = () => {
    setLoading(true);
    const type = status || "ALL";
    PurchaseOrderService.fetchPurchaseOrderList(type, list.request)
      .then((response) => {
        setDataSource(response.data.data);
      })
      .catch((err) => {
        message.error(err.message || "Something went wrong");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleApplyFilters = (values) => {
    const updatedRequest = {
      ...list.request,
      priorityID: values.priority || null,
      supplierID: values.supplierID || null,
      purchaseOrderNum: values.purchaseOrderNum || null,
    };

    setList((prevState) => ({
      ...prevState,
      request: updatedRequest,
    }));
    fetchPurchaseOrderList(updatedRequest);
  };

  const statusMap = {
    5: { color: "gold", text: "Submitted", next: 10 },
    10: { color: "blue", text: "Sent to Supplier", next: 15 },
    15: { color: "purple", text: "Supplier Confirmed for PO", next: 20 },
    20: { color: "orange", text: "QA Booked", next: 25 },
    25: { color: "green", text: "QA Complete", next: null },
  };

  const updateStatus = (orderID, currentStatusID) => {
    const nextStatus = statusMap[currentStatusID]?.next;
    if (!nextStatus) {
      message.warning("No further status available.");
      return;
    }

    PurchaseOrderService.updateStatus(orderID, nextStatus)
      .then(() => {
        message.success("Status updated successfully.");
        fetchPurchaseOrderList();
      })
      .catch((err) => {
        message.error(err.message || "Failed to update status.");
      });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const updatedRequest = {
      ...list.request,
      sortFilter: {
        field: sorter.field || "",
        order:
          sorter.order === "ascend"
            ? "asc"
            : sorter.order === "descend"
              ? "desc"
              : "",
      },
      pageFilter: {
        ...list.request.pageFilter,
        size: pagination.pageSize,
        num: pagination.current - 1,
      },
    };

    setList((prevState) => ({
      ...prevState,
      request: updatedRequest,
    }));
    fetchPurchaseOrderList(updatedRequest);
  };

  const getStatusTag = (statusID, orderID) => {
    const nextStatus = statusMap[statusID]?.next;

    return (
      <Popover
        content={
          nextStatus ? (
            <Button
              type="primary"
              onClick={() => updateStatus(orderID, statusID)}
            >
              Move to {statusMap[nextStatus]?.text}
            </Button>
          ) : (
            <p>No further status</p>
          )
        }
        title="Change Status"
        trigger="click"
      >
        <Tag
          color={statusMap[statusID]?.color || "default"}
          className="cursor-pointer"
        >
          {statusMap[statusID]?.text || "Unknown"}
        </Tag>
      </Popover>
    );
  };

  const priorityOptions = [
    { id: 1, label: "Very High", color: "darkred" },
    { id: 2, label: "High", color: "red" },
    { id: 3, label: "Medium", color: "orange" },
    { id: 4, label: "Low", color: "lightblue" },
    { id: 5, label: "Lowest", color: "green" },
  ];

  const handlePriorityChange = (orderID, priorityID) => {
    PurchaseOrderService.updatePriority(orderID, priorityID)
      .then(() => {
        message.success("Priority updated successfully.");
        fetchPurchaseOrderList();
      })
      .catch((err) => {
        message.error(err.message || "Failed to update priority.");
      });
  };

  const openOrderEnquiry = (orderId) => {
    navigate("/purchase-order-enquiry", {
      state: { orderId: orderId },
    });
  };

  const columns = [
    {
      title: "Order Number",
      dataIndex: "id",
      key: "id",
      render: (orderId) =>
        isLoading ? (
          <Skeleton active size="small" />
        ) : (
          <div className="flex justify-center mx-6">
            <CustomButton
              className="w-1/2"
              type="primary"
              onClick={() => openOrderEnquiry(orderId)}
            >
              {"PO " + orderId}
            </CustomButton>
          </div>
        ),
    },

    {
      title: "Created Date",
      dataIndex: "createDate",
      key: "createDate",
      align: "center",
      render: (text) =>
        isLoading ? (
          <Skeleton active size="small" />
        ) : (
          fromUtc(text).format("DD MMM YYYY")
        ),
    },
    {
      title: "Status",
      dataIndex: "statusID",
      key: "statusID",
      align: "center",
      render: (statusID, record) =>
        isLoading ? (
          <Skeleton active size="small" />
        ) : (
          getStatusTag(statusID, record.id)
        ),
    },
    {
      title: "Priority",
      dataIndex: "priorityID",
      key: "priorityID",
      align: "center",
      sorter: true,
      render: (priorityID, record) =>
        isLoading ? (
          <Skeleton active size="small" />
        ) : (
          <Dropdown
            overlay={
              <Menu>
                {priorityOptions.map((option) => (
                  <Menu.Item
                    key={option.id}
                    onClick={() => handlePriorityChange(record.id, option.id)}
                  >
                    <Tag color={option.color}>{option.label}</Tag>
                  </Menu.Item>
                ))}
              </Menu>
            }
            trigger={["click"]}
          >
            <Tag
              color={
                priorityOptions.find((p) => p.id === priorityID)?.color ||
                "default"
              }
              className="cursor-pointer"
            >
              {priorityOptions.find((p) => p.id === priorityID)?.label ||
                "Unknown"}
            </Tag>
          </Dropdown>
        ),
    },
  ];

  return (
    <>
      <div>
        <FilterDropdown
          onApplyFilters={handleApplyFilters}
          type="purchaseorder"
        />
      </div>
      <Table
        className="mx-0 mt-2 border border-gray-300 rounded-lg shadow-sm"
        columns={columns}
        dataSource={dataSource.map((item, index) => ({ ...item, key: index }))}
        isLoading={isLoading}
        pagination={true}
        onChange={handleTableChange}
      />
    </>
  );
};

export default PurchaseOrderTableComponent;
